import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { settingsService } from '../../../lib/services';

const GDPRSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [activeSection, setActiveSection] = useState('banner');

  // Banner config
  const [bannerEnabled, setBannerEnabled] = useState(true);
  const [bannerPosition, setBannerPosition] = useState('bottom');
  const [bannerStyle, setBannerStyle] = useState('bar');
  const [bannerTitle, setBannerTitle] = useState('We use cookies');
  const [bannerMessage, setBannerMessage] = useState(
    'We use cookies to enhance your browsing experience, serve personalised content, and analyse our traffic. By clicking "Accept All", you consent to our use of cookies.'
  );
  const [acceptLabel, setAcceptLabel] = useState('Accept All');
  const [rejectLabel, setRejectLabel] = useState('Reject Non-Essential');
  const [manageLabel, setManageLabel] = useState('Manage Preferences');
  const [bannerColor, setBannerColor] = useState('#6366f1');
  const [analyticsDefault, setAnalyticsDefault] = useState(true);
  const [marketingDefault, setMarketingDefault] = useState(false);
  const [functionalDefault, setFunctionalDefault] = useState(true);

  // Privacy policy
  const [privacyContent, setPrivacyContent] = useState('');
  const [privacyLastUpdated, setPrivacyLastUpdated] = useState('');

  // GDPR compliance
  const [dpoEmail, setDpoEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [dataRetentionDays, setDataRetentionDays] = useState('365');

  const positionOptions = [
    { value: 'bottom', label: 'Bottom Bar (full width)' },
    { value: 'top', label: 'Top Bar (full width)' },
    { value: 'bottom-left', label: 'Bottom Left Box' },
    { value: 'bottom-right', label: 'Bottom Right Box' },
  ];

  const styleOptions = [
    { value: 'bar', label: 'Bar' },
    { value: 'box', label: 'Box' },
    { value: 'modal', label: 'Modal' },
  ];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await settingsService?.getByCategory('gdpr');
        if (data?.length) {
          data?.forEach(s => {
            switch (s?.settingKey) {
              case 'cookie_consent_enabled': setBannerEnabled(s?.settingValue === 'true'); break;
              case 'cookie_consent_position': setBannerPosition(s?.settingValue || 'bottom'); break;
              case 'cookie_consent_style': setBannerStyle(s?.settingValue || 'bar'); break;
              case 'cookie_consent_title': setBannerTitle(s?.settingValue || 'We use cookies'); break;
              case 'cookie_consent_message': setBannerMessage(s?.settingValue || ''); break;
              case 'cookie_consent_accept_label': setAcceptLabel(s?.settingValue || 'Accept All'); break;
              case 'cookie_consent_reject_label': setRejectLabel(s?.settingValue || 'Reject Non-Essential'); break;
              case 'cookie_consent_manage_label': setManageLabel(s?.settingValue || 'Manage Preferences'); break;
              case 'cookie_consent_color': setBannerColor(s?.settingValue || '#6366f1'); break;
              case 'cookie_analytics_enabled': setAnalyticsDefault(s?.settingValue !== 'false'); break;
              case 'cookie_marketing_enabled': setMarketingDefault(s?.settingValue === 'true'); break;
              case 'cookie_functional_enabled': setFunctionalDefault(s?.settingValue !== 'false'); break;
              case 'privacy_policy_content': setPrivacyContent(s?.settingValue || ''); break;
              case 'privacy_policy_last_updated': setPrivacyLastUpdated(s?.settingValue || ''); break;
              case 'gdpr_dpo_email': setDpoEmail(s?.settingValue || ''); break;
              case 'gdpr_company_name': setCompanyName(s?.settingValue || ''); break;
              case 'gdpr_company_address': setCompanyAddress(s?.settingValue || ''); break;
              case 'gdpr_data_retention_days': setDataRetentionDays(s?.settingValue || '365'); break;
            }
          });
        }
      } catch (e) {
        console.error('Failed to load GDPR settings:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError('');
    try {
      const settings = [
        { settingKey: 'cookie_consent_enabled', settingValue: String(bannerEnabled), settingType: 'boolean', category: 'gdpr' },
        { settingKey: 'cookie_consent_position', settingValue: bannerPosition, settingType: 'string', category: 'gdpr' },
        { settingKey: 'cookie_consent_style', settingValue: bannerStyle, settingType: 'string', category: 'gdpr' },
        { settingKey: 'cookie_consent_title', settingValue: bannerTitle, settingType: 'string', category: 'gdpr' },
        { settingKey: 'cookie_consent_message', settingValue: bannerMessage, settingType: 'string', category: 'gdpr' },
        { settingKey: 'cookie_consent_accept_label', settingValue: acceptLabel, settingType: 'string', category: 'gdpr' },
        { settingKey: 'cookie_consent_reject_label', settingValue: rejectLabel, settingType: 'string', category: 'gdpr' },
        { settingKey: 'cookie_consent_manage_label', settingValue: manageLabel, settingType: 'string', category: 'gdpr' },
        { settingKey: 'cookie_consent_color', settingValue: bannerColor, settingType: 'string', category: 'gdpr' },
        { settingKey: 'cookie_analytics_enabled', settingValue: String(analyticsDefault), settingType: 'boolean', category: 'gdpr' },
        { settingKey: 'cookie_marketing_enabled', settingValue: String(marketingDefault), settingType: 'boolean', category: 'gdpr' },
        { settingKey: 'cookie_functional_enabled', settingValue: String(functionalDefault), settingType: 'boolean', category: 'gdpr' },
        { settingKey: 'privacy_policy_content', settingValue: privacyContent, settingType: 'text', category: 'gdpr' },
        { settingKey: 'privacy_policy_last_updated', settingValue: privacyLastUpdated, settingType: 'string', category: 'gdpr' },
        { settingKey: 'gdpr_dpo_email', settingValue: dpoEmail, settingType: 'string', category: 'gdpr' },
        { settingKey: 'gdpr_company_name', settingValue: companyName, settingType: 'string', category: 'gdpr' },
        { settingKey: 'gdpr_company_address', settingValue: companyAddress, settingType: 'string', category: 'gdpr' },
        { settingKey: 'gdpr_data_retention_days', settingValue: dataRetentionDays, settingType: 'number', category: 'gdpr' },
      ];
      const { error } = await settingsService?.upsert(settings);
      if (error) throw error;
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save GDPR settings');
    } finally {
      setSaving(false);
    }
  };

  const resetConsent = () => {
    localStorage.removeItem('cc_consent');
    alert('Cookie consent has been reset. Refresh the page to see the banner again.');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={24} className="text-primary animate-spin mr-2" />
        <span className="text-muted-foreground">Loading GDPR settings...</span>
      </div>
    );
  }

  const sections = [
    { id: 'banner', label: 'Cookie Banner', icon: 'Cookie' },
    { id: 'privacy', label: 'Privacy Policy', icon: 'FileText' },
    { id: 'compliance', label: 'Compliance', icon: 'ShieldCheck' },
  ];

  return (
    <div className="space-y-6">
      {saveSuccess && (
        <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg text-success text-sm">
          <Icon name="CheckCircle" size={16} />
          GDPR settings saved successfully!
        </div>
      )}
      {saveError && (
        <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
          <Icon name="AlertCircle" size={16} />
          {saveError}
        </div>
      )}
      {/* Section tabs */}
      <div className="flex gap-1 bg-muted/50 p-1 rounded-lg w-fit">
        {sections?.map(s => (
          <button
            key={s?.id}
            onClick={() => setActiveSection(s?.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === s?.id
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={s?.icon} size={14} />
            {s?.label}
          </button>
        ))}
      </div>
      {/* Cookie Banner Section */}
      {activeSection === 'banner' && (
        <div className="space-y-5">
          <div className="bg-card rounded-lg border border-border p-5">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Cookie" size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground mb-1">Cookie Consent Banner</h3>
                <p className="text-sm text-muted-foreground">Configure the GDPR cookie consent banner shown to visitors</p>
              </div>
            </div>
            <div className="space-y-4">
              <Checkbox
                label="Enable Cookie Consent Banner"
                description="Show a cookie consent banner to all visitors"
                checked={bannerEnabled}
                onChange={e => setBannerEnabled(e?.target?.checked)}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select label="Banner Position" options={positionOptions} value={bannerPosition} onChange={setBannerPosition} />
                <Select label="Banner Style" options={styleOptions} value={bannerStyle} onChange={setBannerStyle} />
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <label className="text-sm font-medium text-foreground block mb-1.5">Accent Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={bannerColor}
                      onChange={e => setBannerColor(e?.target?.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-border"
                    />
                    <Input
                      type="text"
                      value={bannerColor}
                      onChange={e => setBannerColor(e?.target?.value)}
                      placeholder="#6366f1"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              <Input label="Banner Title" value={bannerTitle} onChange={e => setBannerTitle(e?.target?.value)} placeholder="We use cookies" />
              <div>
                <label className="text-sm font-medium text-foreground block mb-1.5">Banner Message</label>
                <textarea
                  value={bannerMessage}
                  onChange={e => setBannerMessage(e?.target?.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                  placeholder="Describe your cookie usage..."
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input label="Accept Button Label" value={acceptLabel} onChange={e => setAcceptLabel(e?.target?.value)} />
                <Input label="Reject Button Label" value={rejectLabel} onChange={e => setRejectLabel(e?.target?.value)} />
                <Input label="Manage Button Label" value={manageLabel} onChange={e => setManageLabel(e?.target?.value)} />
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border p-5">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center flex-shrink-0">
                <Icon name="ToggleLeft" size={20} className="text-warning" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground mb-1">Default Cookie Preferences</h3>
                <p className="text-sm text-muted-foreground">Set which cookie categories are enabled by default</p>
              </div>
            </div>
            <div className="space-y-3">
              <Checkbox label="Necessary Cookies (always on)" description="Required for the site to function" checked disabled />
              <Checkbox
                label="Functional Cookies"
                description="Enable enhanced functionality and personalisation"
                checked={functionalDefault}
                onChange={e => setFunctionalDefault(e?.target?.checked)}
              />
              <Checkbox
                label="Analytics Cookies"
                description="Help understand how visitors interact with the site"
                checked={analyticsDefault}
                onChange={e => setAnalyticsDefault(e?.target?.checked)}
              />
              <Checkbox
                label="Marketing Cookies"
                description="Used to deliver relevant advertisements"
                checked={marketingDefault}
                onChange={e => setMarketingDefault(e?.target?.checked)}
              />
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="RefreshCw" size={20} className="text-accent" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground mb-1">Reset Consent</h3>
                  <p className="text-sm text-muted-foreground">Clear stored consent to re-show the banner (useful for testing)</p>
                </div>
              </div>
              <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" onClick={resetConsent}>
                Reset
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Privacy Policy Section */}
      {activeSection === 'privacy' && (
        <div className="space-y-5">
          <div className="bg-card rounded-lg border border-border p-5">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="FileText" size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground mb-1">Privacy Policy Content</h3>
                <p className="text-sm text-muted-foreground">Manage your privacy policy page content</p>
              </div>
            </div>
            <div className="space-y-4">
              <Input
                label="Last Updated Date"
                type="date"
                value={privacyLastUpdated}
                onChange={e => setPrivacyLastUpdated(e?.target?.value)}
                description="Displayed at the top of your privacy policy"
              />
              <div>
                <label className="text-sm font-medium text-foreground block mb-1.5">Privacy Policy Content (HTML)</label>
                <textarea
                  value={privacyContent}
                  onChange={e => setPrivacyContent(e?.target?.value)}
                  rows={16}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground font-mono placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 resize-y"
                  placeholder="<h2>Privacy Policy</h2>&#10;<p>This privacy policy describes how we collect and use your data...</p>"
                />
                <p className="text-xs text-muted-foreground mt-1.5">Supports HTML. This content will be displayed on your /privacy-policy page.</p>
              </div>
            </div>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={18} className="text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Privacy Policy Page</p>
                <p className="text-xs text-muted-foreground">
                  Your privacy policy is accessible at <code className="bg-muted px-1 py-0.5 rounded text-xs">/privacy-policy</code>. 
                  Link to it from your cookie banner and website footer.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Compliance Section */}
      {activeSection === 'compliance' && (
        <div className="space-y-5">
          <div className="bg-card rounded-lg border border-border p-5">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
                <Icon name="ShieldCheck" size={20} className="text-success" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground mb-1">GDPR Compliance Details</h3>
                <p className="text-sm text-muted-foreground">Organisation details required for GDPR compliance</p>
              </div>
            </div>
            <div className="space-y-4">
              <Input
                label="Company / Organisation Name"
                value={companyName}
                onChange={e => setCompanyName(e?.target?.value)}
                placeholder="Acme Ltd"
              />
              <div>
                <label className="text-sm font-medium text-foreground block mb-1.5">Company Address</label>
                <textarea
                  value={companyAddress}
                  onChange={e => setCompanyAddress(e?.target?.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                  placeholder="123 Main Street, City, Country"
                />
              </div>
              <Input
                label="Data Protection Officer (DPO) Email"
                type="email"
                value={dpoEmail}
                onChange={e => setDpoEmail(e?.target?.value)}
                placeholder="dpo@yourcompany.com"
                description="Required under GDPR if you process personal data at scale"
              />
              <Input
                label="Data Retention Period (days)"
                type="number"
                value={dataRetentionDays}
                onChange={e => setDataRetentionDays(e?.target?.value)}
                placeholder="365"
                description="How long user data is retained before automatic deletion"
              />
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border p-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center flex-shrink-0">
                <Icon name="ClipboardCheck" size={20} className="text-warning" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground mb-1">GDPR Checklist</h3>
                <p className="text-sm text-muted-foreground">Key compliance requirements</p>
              </div>
            </div>
            <div className="space-y-2">
              {[
                { label: 'Cookie consent banner enabled', done: bannerEnabled },
                { label: 'Privacy policy content added', done: privacyContent?.length > 50 },
                { label: 'DPO email configured', done: dpoEmail?.length > 0 },
                { label: 'Company details filled in', done: companyName?.length > 0 },
                { label: 'Data retention period set', done: dataRetentionDays?.length > 0 },
              ]?.map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/30">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${item?.done ? 'bg-success/20' : 'bg-muted'}`}>
                    <Icon name={item?.done ? 'Check' : 'Minus'} size={12} className={item?.done ? 'text-success' : 'text-muted-foreground'} />
                  </div>
                  <span className={`text-sm ${item?.done ? 'text-foreground' : 'text-muted-foreground'}`}>{item?.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-3 pt-2">
        <Button variant="outline" onClick={() => window.location?.reload()}>Cancel</Button>
        <Button variant="default" loading={saving} iconName="Save" iconPosition="left" onClick={handleSave}>
          Save GDPR Settings
        </Button>
      </div>
    </div>
  );
};

export default GDPRSettings;
